import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { GetAssociatedFacilitiesQuery } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CredentialFacilityRow } from "./CredentialFacilitiesGrid";
import { CredentialForm, FacilityPickerRow, StaffCredential } from "./types";

export const GET_ASSOCIATED_FACILITIES = gql(`
  query GetAssociatedFacilities($search: String) {
    facilities(search: $search) {
      items {
        id
        name
        streetAddress1
        city
        state
        zip
        lepc {
          id
          name
        }
      }
    }
  }`);

function pickerRowToGridRow(row: FacilityPickerRow): CredentialFacilityRow {
  return {
    id: row.id,
    name: row.name,
    streetAddress1: row.streetAddress1,
    city: row.city,
    state: row.state,
    zip: row.zip,
    lepcName: row.lepc?.name ?? null,
    organizationName: row.tenant?.name ?? null,
    organizationId: row.tenant?.id ?? null,
    staged: "added",
  };
}

/** Flattens the credential's tenant walk into grid rows, folding in staged removals. */
function staffRows(
  credential: StaffCredential | undefined,
  pendingRemoveIds: ReadonlySet<string>
): CredentialFacilityRow[] {
  return (credential?.tenants ?? []).flatMap((tenant) =>
    tenant.facilities.map((facility) => ({
      id: facility.id,
      name: facility.name,
      streetAddress1: facility.streetAddress1,
      city: facility.city,
      state: facility.state,
      zip: facility.zip,
      lepcName: facility.lepcName,
      organizationName: tenant.name,
      organizationId: tenant.id,
      staged: pendingRemoveIds.has(facility.id) ? ("removed" as const) : null,
    }))
  );
}

function customerRows(
  facilityIds: string[],
  fetched: GetAssociatedFacilitiesQuery | undefined
): CredentialFacilityRow[] {
  const linked = new Set(facilityIds);
  return (fetched?.facilities?.items ?? [])
    .filter((facility) => linked.has(facility.id))
    .map((facility) => ({
      id: facility.id,
      name: facility.name,
      streetAddress1: facility.streetAddress1,
      city: facility.city,
      state: facility.state,
      zip: facility.zip,
      lepcName: facility.lepc?.name ?? null,
      staged: null,
    }));
}

/**
 * Facility changes the staff surface holds until Save, so the whole set can be confirmed
 * at once. Nothing here runs for the customer surface, which writes straight into
 * `facilityIds` and lets its upsert replace the set wholesale.
 */
function useStaffStaging(storedFacilityIds: ReadonlySet<string>) {
  const [pendingAdds, setPendingAdds] = useState<CredentialFacilityRow[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<ReadonlySet<string>>(new Set());

  const toggleStaged = useCallback(
    (facilityId: string) => {
      if (pendingAdds.some((facility) => facility.id === facilityId)) {
        setPendingAdds((previous) => previous.filter((facility) => facility.id !== facilityId));
        return;
      }
      setPendingRemoveIds((previous) => {
        const next = new Set(previous);
        if (next.has(facilityId)) next.delete(facilityId);
        else next.add(facilityId);
        return next;
      });
    },
    [pendingAdds]
  );

  /** Adds only. Re-adding cancels a pending removal rather than stacking on it. */
  const stageAdds = useCallback(
    (rows: FacilityPickerRow[]) => {
      const reAdded = rows.filter((row) => pendingRemoveIds.has(row.id));
      if (reAdded.length) {
        setPendingRemoveIds((previous) => {
          const next = new Set(previous);
          for (const row of reAdded) next.delete(row.id);
          return next;
        });
      }
      const fresh = rows.filter(
        (row) => !storedFacilityIds.has(row.id) && !pendingRemoveIds.has(row.id)
      );
      if (!fresh.length) return;
      setPendingAdds((previous) => {
        const known = new Set(previous.map((facility) => facility.id));
        return [...previous, ...fresh.filter((row) => !known.has(row.id)).map(pickerRowToGridRow)];
      });
    },
    [pendingRemoveIds, storedFacilityIds]
  );

  const clearStaged = useCallback(() => {
    setPendingAdds([]);
    setPendingRemoveIds(new Set());
  }, []);

  return { pendingAdds, pendingRemoveIds, toggleStaged, stageAdds, clearStaged };
}

/** The facilities a staff credential already holds, out of its tenant walk. */
export function staffFacilityIds(credential: StaffCredential | undefined): string[] {
  return (credential?.tenants ?? []).flatMap((tenant) =>
    tenant.facilities.map((facility) => facility.id)
  );
}

export function useCredentialFacilities({
  form,
  isCustomer,
  staffCredential,
  tenantId,
}: {
  form: CredentialForm;
  isCustomer: boolean;
  staffCredential: StaffCredential | undefined;
  tenantId?: string;
}) {
  const { setValue, watch } = form;

  const watchedFacilityIds = watch("facilityIds");
  const customerFacilityIds = useMemo(() => watchedFacilityIds ?? [], [watchedFacilityIds]);

  const storedStaffFacilityIds = useMemo(
    () => new Set(staffFacilityIds(staffCredential)),
    [staffCredential]
  );

  const staging = useStaffStaging(storedStaffFacilityIds);

  // Sorted so reordering the same set doesn't produce new variables and refetch.
  const associatedQuery = useQuery(GET_ASSOCIATED_FACILITIES, {
    variables: { search: `tenantId:${tenantId} ids:${[...customerFacilityIds].sort().join(",")}` },
    skip: !isCustomer || customerFacilityIds.length === 0,
  });

  // `previousData` while a new id set is in flight: the rows are filtered to the current
  // ids anyway, so a removal drops out at once and an add appears when the fetch lands —
  // rather than the whole grid blanking on every change.
  const fetchedFacilities = associatedQuery.data ?? associatedQuery.previousData;

  const facilityRows = useMemo(() => {
    if (isCustomer) return customerRows(customerFacilityIds, fetchedFacilities);
    // Staged adds go last rather than into their organization's block, so a facility just
    // picked is where the user can see it.
    return [...staffRows(staffCredential, staging.pendingRemoveIds), ...staging.pendingAdds];
  }, [
    isCustomer,
    customerFacilityIds,
    fetchedFacilities,
    staffCredential,
    staging.pendingRemoveIds,
    staging.pendingAdds,
  ]);

  /**
   * Deliberately not derived from the watched field it feeds: the effect below writes this
   * back into `facilityIds`, and depending on the result would re-run itself forever.
   */
  const stagedStaffFacilityIds = useMemo(() => {
    const ids = new Set(storedStaffFacilityIds);
    for (const add of staging.pendingAdds) ids.add(add.id);
    for (const id of staging.pendingRemoveIds) ids.delete(id);
    return [...ids];
  }, [storedStaffFacilityIds, staging.pendingAdds, staging.pendingRemoveIds]);

  /**
   * The staff grid stages links outside the form, but the save sends them as the list it
   * writes and derives tenancy from.
   *
   * Skipped when nothing moved: the memo recomputes on every `cache-and-network` refetch, and
   * `shouldValidate` would spend a debounced server round-trip re-asking about the same ids.
   */
  useEffect(() => {
    if (isCustomer) return;
    const unchanged =
      watchedFacilityIds?.length === stagedStaffFacilityIds.length &&
      stagedStaffFacilityIds.every((id) => watchedFacilityIds.includes(id));
    if (unchanged) return;
    setValue("facilityIds", stagedStaffFacilityIds, { shouldValidate: true });
  }, [isCustomer, stagedStaffFacilityIds, watchedFacilityIds, setValue]);

  /** What the credential will hold once saved — what the picker must not re-offer. */
  const linkedFacilityIds = useMemo(
    () => new Set(isCustomer ? customerFacilityIds : stagedStaffFacilityIds),
    [isCustomer, customerFacilityIds, stagedStaffFacilityIds]
  );

  /** One control for both directions, so every staged change is undoable before Save. */
  const toggleFacility = useCallback(
    (facilityId: string) => {
      if (!isCustomer) {
        staging.toggleStaged(facilityId);
        return;
      }
      // Dirty, or the on-focus refetch puts the removed link back.
      setValue(
        "facilityIds",
        customerFacilityIds.filter((id) => id !== facilityId),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [isCustomer, staging, setValue, customerFacilityIds]
  );

  /** Customer picker: a full replacement set, which its upsert writes as one field. */
  const applyCustomerSelection = useCallback(
    (facilityIds: string[]) => {
      setValue("facilityIds", facilityIds, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  return {
    customerFacilityIds,
    facilityRows,
    facilitiesLoading: associatedQuery.loading,
    linkedFacilityIds,
    pendingAdds: staging.pendingAdds,
    pendingRemoveIds: staging.pendingRemoveIds,
    stagedRemovals: facilityRows.filter((row) => row.staged === "removed"),
    pendingCount: isCustomer ? 0 : staging.pendingAdds.length + staging.pendingRemoveIds.size,
    toggleFacility,
    applyCustomerSelection,
    stageStaffAdds: staging.stageAdds,
    clearStaged: staging.clearStaged,
  };
}
